import { useState, useRef } from 'react'
import { useShoppingItems } from '../hooks/useShoppingItems'
import { Spinner } from '../../../components/ui/Spinner'
import { Button } from '../../../components/ui/Button'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'

const SwipeItem = ({ item, onCheck, onDelete }) => {
  const startX  = useRef(null)
  const [offset, setOffset] = useState(0)
  const [swiped, setSwiped] = useState(false)

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX }
  const onTouchMove  = (e) => {
    const dx = e.touches[0].clientX - startX.current
    if (dx < 0) setOffset(Math.max(dx, -72))
  }
  const onTouchEnd = () => {
    if (offset < -48) { setSwiped(true); setOffset(-72) }
    else { setSwiped(false); setOffset(0) }
  }

  const reset = () => { setSwiped(false); setOffset(0) }

  return (
    <div className="relative overflow-hidden">
      {/* Delete button behind */}
      <div className="absolute inset-y-0 right-0 w-18 flex items-center justify-center bg-rose-500">
        <button onClick={onDelete} className="text-white px-4 py-2 text-sm font-semibold" style={{ width: 72 }}>
          Delete
        </button>
      </div>

      {/* Item row */}
      <div
        className="relative bg-surface flex items-center gap-3 px-4 py-3 transition-transform"
        style={{ transform: `translateX(${offset}px)` }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={swiped ? reset : undefined}
      >
        <button
          onClick={(e) => { e.stopPropagation(); onCheck() }}
          className={cn(
            'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors',
            item.checked_at ? 'bg-emerald-400 border-emerald-400' : 'border-border',
          )}
        >
          {item.checked_at && (
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5">
              <path d="m2 6 3 3 5-5" />
            </svg>
          )}
        </button>

        <div className="flex-1 min-w-0">
          <span className={cn('text-sm', item.checked_at && 'line-through text-text-muted')}>
            {item.name}
          </span>
          {item.quantity && (
            <span className="text-xs text-text-muted ml-2">{item.quantity}</span>
          )}
        </div>

        {item.store && (
          <span className="text-[11px] text-text-muted bg-background px-2 py-0.5 rounded-full flex-shrink-0">
            {item.store}
          </span>
        )}
      </div>
    </div>
  )
}

const AddItemForm = ({ onAdd }) => {
  const [name,     setName]     = useState('')
  const [quantity, setQuantity] = useState('')
  const [store,    setStore]    = useState('')
  const [category, setCategory] = useState('')
  const [expanded, setExpanded] = useState(false)
  const [adding,   setAdding]   = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return
    setAdding(true)
    try {
      await onAdd({ name: name.trim(), quantity, store, category })
      setName(''); setQuantity(''); setStore(''); setCategory('')
      setExpanded(false)
    } finally { setAdding(false) }
  }

  return (
    <form onSubmit={handleSubmit} className="border-t border-border bg-surface px-4 py-3">
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Add item…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onFocus={() => setExpanded(true)}
          className="flex-1 rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <Button type="submit" size="sm" disabled={adding || !name.trim()}>
          {adding ? <Spinner size="sm" /> : 'Add'}
        </Button>
      </div>

      {expanded && (
        <div className="grid grid-cols-3 gap-2 mt-2">
          <input
            type="text"
            placeholder="Qty"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="rounded-lg border border-border px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <input
            type="text"
            placeholder="Store"
            value={store}
            onChange={(e) => setStore(e.target.value)}
            className="rounded-lg border border-border px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <input
            type="text"
            placeholder="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-lg border border-border px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      )}
    </form>
  )
}

export const ShoppingListView = ({ list, recipes, onBack }) => {
  const { data: items = [], isLoading, addItem, addItems, checkItem, deleteItem, clearChecked } = useShoppingItems(list.id)
  const [showRecipePicker, setShowRecipePicker] = useState(false)
  const [clearing, setClearing] = useState(false)

  const grouped = items.reduce((acc, item) => {
    const key = item.category || 'Other'
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})

  const checkedCount = items.filter((i) => i.checked_at).length

  const handleClear = async () => {
    setClearing(true)
    try { await clearChecked() } finally { setClearing(false) }
  }

  const handleAddFromRecipe = async (recipe) => {
    const ingredients = (recipe.ingredients ?? []).map((ing) => ({
      name:     ing.name,
      quantity: [ing.amount, ing.unit].filter(Boolean).join(' ') || null,
      category: 'From Recipe',
    }))
    await addItems(ingredients)
    setShowRecipePicker(false)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-surface flex-shrink-0">
        <button onClick={onBack} className="p-1 text-text-muted hover:text-text-primary">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <h1 className="text-base font-bold text-text-primary flex-1">{list.name}</h1>
        <div className="flex gap-2">
          {checkedCount > 0 && (
            <Button variant="ghost" size="sm" onClick={handleClear} disabled={clearing}>
              {clearing ? <Spinner size="sm" /> : `Clear ${checkedCount} checked`}
            </Button>
          )}
          <button
            onClick={() => setShowRecipePicker(true)}
            className="text-sm text-primary font-medium hover:opacity-80"
            title="Add from recipe"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="22" />
            </svg>
          </button>
        </div>
      </div>

      {/* Items */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : items.length === 0 ? (
          <p className="text-center text-text-muted text-sm py-12">No items yet. Add one below.</p>
        ) : (
          Object.entries(grouped).map(([category, catItems]) => (
            <div key={category}>
              <div className="px-4 py-1.5 bg-background text-xs font-semibold text-text-muted uppercase tracking-wide">
                {category}
              </div>
              {catItems.map((item) => (
                <SwipeItem
                  key={item.id}
                  item={item}
                  onCheck={() => checkItem(item)}
                  onDelete={() => deleteItem(item.id)}
                />
              ))}
            </div>
          ))
        )}
      </div>

      {/* Add form */}
      <AddItemForm onAdd={addItem} />

      {/* Recipe picker modal */}
      {showRecipePicker && (
        <Portal>
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={() => setShowRecipePicker(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[60dvh] flex flex-col shadow-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
              <h3 className="font-bold text-text-primary">Add from Recipe</h3>
              <button onClick={() => setShowRecipePicker(false)} className="text-text-muted p-1">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 min-h-0 py-2">
              {(recipes ?? []).length === 0 ? (
                <p className="text-sm text-text-muted text-center py-8">No recipes yet</p>
              ) : (
                (recipes ?? []).map((r) => (
                  <button
                    key={r.id}
                    onClick={() => handleAddFromRecipe(r)}
                    className="w-full text-left px-5 py-3 hover:bg-background text-sm font-medium text-text-primary"
                  >
                    {r.title}
                    {r.ingredients?.length > 0 && (
                      <span className="text-text-muted ml-2 font-normal text-xs">{r.ingredients.length} ingredients</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  )
}
